/**
 * API Service Layer for Relay Protocol Frontend
 * Implements error handling, authentication, and type-safe API calls
 * Following 2024 best practices for React/TypeScript applications
 */

import React from 'react';

export interface ApiError {
  status: number;
  message: string;
  details?: any;
  timestamp: string;
}

export interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
  status: 'success' | 'error';
}

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  retries?: number;
}

class ApiService {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private authToken: string | null = null;

  constructor(baseUrl: string = '') {
    // Resolve API base URL from env (preferred) or fallback detection.
    // Expect a full URL including prefix, e.g. http://localhost:7110/api/v1
    // Prefer Vite env; avoid referencing Node's process in browser contexts
    const envUrl = (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_API_URL)
      || (typeof window !== 'undefined' ? (window as any).__RELAY_API_URL : undefined);
    this.baseUrl = baseUrl || envUrl || this.detectApiBaseUrl();
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Load auth token from localStorage if available
    this.loadAuthToken();

    // Dev convenience: allow env/window to set an API key for local demo
    if (!this.authToken) {
      const envKey = (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_DEV_API_KEY)
        || (typeof window !== 'undefined' ? (window as any).__RELAY_API_KEY : undefined);
      if (envKey) {
        this.setAuthToken(envKey);
      }
    }
  }

  private detectApiBaseUrl(): string {
    // Development/local environment
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      // Default dev ports reserved for Relay Protocol
      return 'http://localhost:7110/api/v1';
    }
    // Production: assume same-origin reverse proxy exposes /api/v1
    return '/api/v1';
  }

  private loadAuthToken(): void {
    try {
      const token = localStorage.getItem('relay_auth_token');
      if (token) {
        this.authToken = token;
      }
    } catch (error) {
      console.warn('Failed to load auth token from localStorage:', error);
    }
  }

  public setAuthToken(token: string | null): void {
    this.authToken = token;
    if (token) {
      try {
        localStorage.setItem('relay_auth_token', token);
      } catch (error) {
        console.warn('Failed to save auth token to localStorage:', error);
      }
    } else {
      try {
        localStorage.removeItem('relay_auth_token');
      } catch (error) {
        console.warn('Failed to remove auth token from localStorage:', error);
      }
    }
  }

  private getHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const headers = { ...this.defaultHeaders, ...customHeaders };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  private async makeRequest<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers: customHeaders,
      body,
      timeout = 30000,
      retries = 3
    } = config;

    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    const headers = this.getHeaders(customHeaders);

    const requestInit: RequestInit = {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };

    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...requestInit,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;

          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { message: errorText || `HTTP ${response.status}` };
          }

          return {
            status: 'error',
            error: {
              status: response.status,
              message: errorData.detail || errorData.message || `Request failed with status ${response.status}`,
              details: errorData,
              timestamp: new Date().toISOString()
            }
          };
        }

        const responseText = await response.text();
        let data: T;

        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (error) {
          console.warn('Failed to parse JSON response:', error);
          data = responseText as any;
        }

        return {
          status: 'success',
          data
        };

      } catch (error) {
        lastError = error as Error;

        // Don't retry on certain error types
        if (error instanceof TypeError || (error as any).name === 'AbortError') {
          break;
        }

        // Wait before retrying (exponential backoff)
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }

    return {
      status: 'error',
      error: {
        status: 0,
        message: lastError?.message || 'Network request failed',
        details: lastError,
        timestamp: new Date().toISOString()
      }
    };
  }

  // Generic HTTP methods
  public async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'GET' });
  }

  public async post<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'POST', body });
  }

  public async put<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PUT', body });
  }

  public async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'DELETE' });
  }

  public async patch<T>(endpoint: string, body?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PATCH', body });
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Helper function for handling API responses in components
export function handleApiResponse<T>(
  response: ApiResponse<T>,
  onSuccess?: (data: T) => void,
  onError?: (error: ApiError) => void
): boolean {
  if (response.status === 'success' && response.data !== undefined) {
    onSuccess?.(response.data);
    return true;
  } else if (response.error) {
    onError?.(response.error);
    return false;
  }
  return false;
}

// Hook for API calls with React patterns
export function useApiCall() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<ApiError | null>(null);

  const callApi = async <T>(
    apiCall: () => Promise<ApiResponse<T>>,
    onSuccess?: (data: T) => void,
    onError?: (error: ApiError) => void
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiCall();

      if (response.status === 'success' && response.data !== undefined) {
        onSuccess?.(response.data);
        return true;
      } else if (response.error) {
        setError(response.error);
        onError?.(response.error);
        return false;
      }
      return false;
    } catch (err) {
      const error: ApiError = {
        status: 0,
        message: err instanceof Error ? err.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
      setError(error);
      onError?.(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, callApi };
}

export default apiService;
